import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Bill, ILineItem } from '@feathr/blackbox';
import type { ITanStackTableProps } from '@feathr/components';
import { TanStackTable } from '@feathr/components';

import type { TBillRow } from './BillColumns';
import BillColumns from './BillColumns';

interface IProps {
  bill: Bill;
  buildLineItemUrl?: (eventId: string, campaignId: string) => string;
}

// Derive the sort type from TanStackTable's own props: the `@feathr/components` barrel `ISorted`
// resolves to the legacy `Table`'s narrower `desc: true`, which the TanStack `desc: boolean`
// (false included) does not satisfy.
type TBillSort = NonNullable<ITanStackTableProps<TBillRow>['sort']>;

function BillTable({ bill, buildLineItemUrl }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const [sort, setSort] = useState<TBillSort>([{ id: 'description' }]);

  // Client-side sort: the table sorts manually, so BillTable owns the row order. Each row also
  // carries a synthetic `id` from its original position (see TBillRow) — no ILineItem field is a
  // unique row key, and the position stays stable across the sorts below.
  const lineItems: ILineItem[] = bill.isPending ? [] : bill.get('line_items', []);
  let sortedItems: TBillRow[] = lineItems.map((item, index) => ({ ...item, id: String(index) }));
  sort.forEach((s) => {
    if (s.id === 'description') {
      // Cannot sort observable in place, so make a copy using slice().
      sortedItems = sortedItems
        .slice()
        .sort((a, b) =>
          s.desc
            ? b.description.localeCompare(a.description)
            : a.description.localeCompare(b.description),
        );
    } else if (s.id === 'price') {
      // Cannot sort observable in place, so make a copy using slice().
      sortedItems = sortedItems
        .slice()
        .sort((a, b) => (s.desc ? b.price - a.price : a.price - b.price));
    }
  });

  function handleSortChange(newSort: TBillSort): void {
    // This table sorts one column at a time. TanStack's multi-sort (shift-click) is on by default,
    // so keep only the newest entry — otherwise a stale primary key would re-sort on top of the
    // active one and silently produce the wrong order.
    setSort(newSort.slice(-1));
  }

  return (
    <TanStackTable<TBillRow>
      columns={BillColumns(t, bill.get('event_id'), buildLineItemUrl)}
      idKey={'id'}
      isLoading={bill.isPending}
      isPaginated={false}
      items={sortedItems}
      noDataText={t('No data')}
      onSortChange={handleSortChange}
      sort={sort}
    />
  );
}

export default observer(BillTable);
